import type { PosDB } from "./db.js";
import { GENESIS, verifyChain } from "./hash.js";
import { applyResult, chainLink, chainRange, isRefusal, pendingSales, terminalId } from "./queue.js";
import type { QueuedSale, SyncResult } from "./types.js";

const BASE_MS = 2_000;
/** Small batches keep the upload lock short, so a live sale at the counter never waits long behind a backlog. */
const BATCH = 25;
const MAX_MS = 5 * 60_000;

/** Exponential backoff with up to 20% jitter: 2 s, 4 s, 8 s … capped at 5 minutes. */
export function backoffDelay(attempt: number, rand: () => number = Math.random): number {
  const d = Math.min(BASE_MS * 2 ** Math.max(0, attempt), MAX_MS);
  return Math.round(d * (1 + 0.2 * rand()));
}

/**
 * Wait for `work` at most `ms`, then settle for `fallback`; the work carries on and its result is still recorded.
 * Used so a slow server or an upload holding the lock never keeps a devotee waiting for the slip.
 */
export function within<T>(work: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<T>((ok) => { timer = setTimeout(() => ok(fallback), ms); });
  return Promise.race([work, late]).finally(() => clearTimeout(timer));
}

/** Body item of POST /api/counters/sync. */
export const toSyncSale = (s: QueuedSale, terminal: string) =>
  ({ clientRef: s.clientRef, createdAt: s.createdAt, cart: s.cart, payments: s.payments, payer: s.payer, chain: chainLink(s, terminal) });
export type SyncSale = ReturnType<typeof toSyncSale>;

/** Uploads a batch; throws when the network or server is unreachable (the batch stays queued). */
export type Uploader = (sales: SyncSale[]) => Promise<SyncResult[]>;

/** retry: sales the server answered "not now" (busy, no open shift, device binding); they stay queued. */
export interface SyncOutcome { uploaded: number; ok: number; rejected: number; retry: number; retryError?: string; blocked?: { clientRef: string; reason: string } }

/**
 * Upload pending sales once, oldest first. The batch, and the sale just before it (the anchor its first hash links
 * to), must verify, otherwise nothing is sent: deleting the oldest waiting sale is caught here. The server is
 * idempotent on clientRef, so a batch interrupted half-way is safe to resend.
 */
export async function syncOnce(db: PosDB, upload: Uploader, cashier: string): Promise<SyncOutcome> {
  const none = { uploaded: 0, ok: 0, rejected: 0, retry: 0 };
  const batch = await pendingSales(db, cashier, BATCH);
  if (!batch.length) return none;
  const first = batch[0]!;
  const segment = await chainRange(db, Math.max(1, first.n - 1), batch[batch.length - 1]!.n);
  const anchored = first.n === 1 ? first.prevHash === GENESIS : segment[0]?.n === first.n - 1;
  if (!anchored) return { ...none, blocked: { clientRef: first.clientRef, reason: "The sale saved just before it is missing." } };
  const check = await verifyChain(segment);
  if (!check.ok) return { ...none, blocked: { clientRef: check.clientRef, reason: check.reason } };
  const terminal = await terminalId(db);
  const results = await upload(batch.map((s) => toSyncSale(s, terminal)));
  const out: SyncOutcome = { ...none, uploaded: batch.length };
  for (const r of results) {
    if (!batch.some((s) => s.clientRef === r.clientRef)) continue;
    await applyResult(db, r);
    if (r.status !== "error") out.ok++;
    else if (isRefusal(r.error?.code)) out.rejected++;
    else { out.retry++; out.retryError ??= r.error?.message; }
  }
  return out;
}

export interface SyncState {
  pending: number;
  running: boolean;
  attempt: number;
  nextAt: number | null;
  lastSyncAt: number | null;
  lastError: string | null;
  blocked: { clientRef: string; reason: string } | null;
}

interface EngineDeps {
  db: PosDB;
  upload: Uploader;
  cashier: () => string | null;       // the signed-in cashier; nothing uploads while signed out
  isNetworkError: (e: unknown) => boolean;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (h: unknown) => void;
  rand?: () => number;
}

/**
 * Background sync: runs whenever sales are waiting, retries with exponential backoff while the server is
 * unreachable, and runs immediately when the browser reports it is back online. All uploads share one lock, so a
 * sale is never sent twice at the same time.
 */
export class SyncEngine {
  state: SyncState = { pending: 0, running: false, attempt: 0, nextAt: null, lastSyncAt: null, lastError: null, blocked: null };
  private timer: unknown = null;
  private lock: Promise<unknown> = Promise.resolve();
  private listeners = new Set<(s: SyncState) => void>();
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (h: unknown) => void;

  constructor(private readonly deps: EngineDeps) {
    this.now = deps.now ?? Date.now;
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  subscribe(fn: (s: SyncState) => void): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private set(patch: Partial<SyncState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn(this.state);
  }

  /** Run under the upload lock; one thing talks to the server at a time. */
  exclusive<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.lock.then(fn, fn);
    this.lock = run.catch(() => undefined);
    return run;
  }

  async refreshCount(): Promise<void> {
    const cashier = this.deps.cashier();
    this.set({ pending: cashier ? await this.deps.db.sales.where("status").equals("pending").filter((s) => s.cashier === cashier).count() : 0 });
  }

  /** Sync now (after a sale, on reconnect, or from the retry button). */
  kick(): Promise<void> {
    if (this.timer) this.clearTimer(this.timer);
    this.timer = null;
    return this.exclusive(() => this.run());
  }

  private async run(): Promise<void> {
    const cashier = this.deps.cashier();
    if (!cashier) return;
    this.set({ running: true, nextAt: null });
    try {
      const out = await syncOnce(this.deps.db, this.deps.upload, cashier);
      await this.refreshCount();
      if (out.blocked) {
        this.set({ running: false, blocked: out.blocked, lastError: out.blocked.reason });
        return;
      }
      if (out.uploaded && !out.ok && !out.rejected) { // the server deferred every sale: back off, do not hammer it
        this.set({ running: false, blocked: null, lastError: out.retryError ?? "The server asked to try again later." });
        this.retryLater();
        return;
      }
      this.set({ running: false, attempt: 0, blocked: null, lastError: out.retry ? out.retryError ?? null : null, lastSyncAt: out.uploaded ? this.now() : this.state.lastSyncAt });
      if (this.state.pending > 0 && out.uploaded > 0) this.schedule(0); // more than one batch waiting
    } catch (e) {
      await this.refreshCount();
      this.set({ running: false, lastError: this.deps.isNetworkError(e) ? "Server not reachable; sales are safe on this terminal." : (e as Error).message });
      this.retryLater();
    }
  }

  /** After a failed upload: back off, then try again. */
  private retryLater(): void {
    const attempt = this.state.attempt;
    this.set({ attempt: attempt + 1 });
    this.schedule(backoffDelay(attempt, this.deps.rand));
  }

  /**
   * A bill was saved without reaching the server: make sure an upload is on the way. Never moves a retry that is
   * already scheduled and never raises the backoff (only failed uploads do), so steady billing cannot starve the queue.
   */
  ensureScheduled(): void {
    if (this.timer || this.state.running) return;
    this.schedule(backoffDelay(this.state.attempt, this.deps.rand));
  }

  private schedule(ms: number) {
    if (this.timer) this.clearTimer(this.timer);
    this.set({ nextAt: this.now() + ms });
    this.timer = this.setTimer(() => { this.timer = null; void this.kick(); }, ms);
  }

  stop(): void {
    if (this.timer) this.clearTimer(this.timer);
    this.timer = null;
  }
}
